import { useId } from 'react';
import type React from 'react';

import { FocusRing, Text } from '@change/ds-core-ui';
import { TextArea, TextField } from '@change/ds-forms';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { ContactDecisionMakerSendFailure } from '#src/app/shared/utils/contactDecisionMaker';

import { ComposeSheet } from '../ComposeSheet';
import type { ContactDecisionMakerStageProps } from '../types';

import { RecipientCard } from './components/RecipientCard';

/**
 * The failed-send copy. Separate `Translate` elements rather than a computed key: the i18n build
 * plugin extracts keys statically and rejects anything it cannot read off the call.
 */
const SendFailureNotice: React.FC<{ failure: ContactDecisionMakerSendFailure; decisionMakerName: string }> = ({
	failure,
	decisionMakerName,
}) => {
	switch (failure) {
		case 'blocked':
			return <Translate value="corgi.contact_decision_maker.review.send_blocked" />;
		case 'unconfirmed':
			return <Translate value="corgi.contact_decision_maker.review.send_unconfirmed" />;
		case 'already_sent':
			return (
				<Translate
					value="corgi.contact_decision_maker.review.send_already_sent"
					replacements={{ decision_maker_name: decisionMakerName }}
				/>
			);
		case 'rejected':
			return <Translate value="corgi.contact_decision_maker.review.send_rejected" />;
		default:
			return <Translate value="corgi.contact_decision_maker.review.send_failed" />;
	}
};

/**
 * Screen 4, review sub-states (CHANGE-97532). One screen with three states: the read-only draft, the
 * editor, and the in-flight send.
 *
 * The read-only state is load-bearing rather than decorative. Research found the flow's main risk to
 * be uncritical adoption — sending an AI draft unread — so `send` is only reachable from here, never
 * from the editor, and the machine enforces it.
 */
export const Preview: React.FC<ContactDecisionMakerStageProps> = ({ decisionMaker, flow }) => {
	const { translate } = useI18n();
	const { stage, draft, sendFailure } = flow.data;
	const { setDraft, editDraft, finishEditing, send, dismiss, backToStory } = flow.actions;

	const hintId = useId();
	const isEditing = stage === 'preview:editing';
	const isSending = stage === 'sending';
	// An empty draft is guaranteed to fail on `MESSAGE_BLANK`, and the read-only card takes the draft
	// as its accessible name, so an empty one also leaves that button unnamed.
	const isDraftEmpty = draft.trim().length === 0;
	// Every failure except a plain transport one holds the send button off: a rejected message fails
	// identically until the draft changes (which clears this), and the sticky ones (`isStickySendFailure`)
	// are not about the body at all — the reducer keeps those across an edit and a trip back to the story.
	const blocksSend = sendFailure !== undefined && sendFailure !== 'retryable';

	return (
		<ComposeSheet
			isOpen
			fullHeightOnMobile
			onOpenChange={(isOpen) => {
				if (!isOpen) dismiss();
			}}
			eyebrow={
				isEditing
					? translate('corgi.contact_decision_maker.review.editing_eyebrow')
					: translate('corgi.contact_decision_maker.review.eyebrow')
			}
			heading={translate('corgi.contact_decision_maker.review.heading')}
			// Read-only review only: the machine ignores it elsewhere, and a back arrow while editing or
			// sending would promise something that does not happen.
			back={
				isEditing || isSending
					? undefined
					: { onClick: backToStory, ariaLabel: translate('corgi.contact_decision_maker.review.back') }
			}
			primaryAction={
				isEditing
					? {
							label: translate('corgi.contact_decision_maker.review.done_cta'),
							onClick: finishEditing,
							disabled: isDraftEmpty,
						}
					: {
							label: translate('corgi.contact_decision_maker.review.send_cta'),
							onClick: send,
							loading: isSending,
							disabled: isDraftEmpty || blocksSend,
						}
			}
		>
			<RecipientCard decisionMaker={decisionMaker} />

			{/* Not from the prototype — the spec has no failed-send state. Offered because the supporter has
			    already written this message, and dropping them out of the flow would discard it. A
			    retryable failure offers the same message again; a rejection asks for it to be edited. */}
			{sendFailure && !isEditing && (
				<Text
					role="alert"
					className="text-size-body-small text-typography-error mt-3"
					data-testid="contact-decision-maker-send-failed"
				>
					<SendFailureNotice failure={sendFailure} decisionMakerName={decisionMaker.displayName} />
				</Text>
			)}

			{isEditing ? (
				<TextField
					label={translate('corgi.contact_decision_maker.review.draft_label')}
					labelIsVisible={false}
					name="contact-decision-maker-draft"
					className="mt-3"
					value={draft}
					onChange={setDraft}
					autoFocus
					input={<TextArea className="min-h-[220px] resize-none font-serif" />}
				/>
			) : (
				<>
					{/* A button, not a div with a click handler: the whole card is the edit affordance. Its
					    accessible name is the draft itself, which is the useful thing to hear, with the hint
					    below attached as the description. */}
					<FocusRing focusRingClass="rounded-md">
						<button
							type="button"
							onClick={editDraft}
							aria-describedby={hintId}
							disabled={isSending}
							className="border-stroke-weaker elevation-low mt-3 w-full cursor-text rounded-md border border-solid p-4 text-left"
							data-testid="contact-decision-maker-draft"
						>
							{/* `pre-line` so the draft's paragraph breaks survive — it arrives as plain text with
							    newlines, not markup. */}
							<Text className="text-size-body-small text-typography-strong font-serif whitespace-pre-line">
								{draft}
							</Text>
						</button>
					</FocusRing>
					<Text id={hintId} className="text-size-body-xs text-typography-weak mt-3 text-center">
						<Translate value="corgi.contact_decision_maker.review.edit_hint" />
					</Text>
				</>
			)}
		</ComposeSheet>
	);
};
